import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Chip,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TollIcon from "@mui/icons-material/Toll";
import { toast } from "react-toastify";
import axiosInstance from "../../../../api/axiosConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCancel, faCheck } from "@fortawesome/free-solid-svg-icons";

function MemberDetailsModal({ open, onClose, member }) {
  const PAGE_SIZE = 10;

  const [walletBalance, setWalletBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [activeTab, setActiveTab] = useState(1);

  // Transactions State
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Orders State
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotalPages, setOrdersTotalPages] = useState(1);

  const [tokenBalances, setTokenBalances] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(false);

  const user = member?.user || {};

  // Fetch Member Wallet Balance (GNGN)
  const fetchWalletBalance = useCallback(async () => {
    if (!user?._id) return;

    try {
      setLoadingBalance(true);

      const response = await axiosInstance.get(
        "/marketplace/getequity/get-member-wallet-balance/",
        {
          params: {
            member_id: user._id,
          },
        },
      );

      if (response.data?.status === "successful") {
        setWalletBalance(response.data.data?.GNGN ?? 0);
      }
    } catch (error) {
      console.error("Error fetching wallet balance: ", error);

      toast.error(
        error.response?.data?.message || "Failed to load wallet balance.",
      );
    } finally {
      setLoadingBalance(false);
    }
  }, [user?._id]);

  // Fetch Member Token Balances
  const fetchMemberTokenBalances = useCallback(async () => {
    if (!user?._id) return;

    try {
      setLoadingTokens(true);

      const response = await axiosInstance.get(
        "/marketplace/getequity/get-member-token-balance/",
        {
          params: {
            member_id: user._id,
          },
        },
      );

      if (
        response.data?.status === "successful" &&
        Array.isArray(response.data?.data)
      ) {
        setTokenBalances(response.data.data);
      } else {
        setTokenBalances([]);
      }
    } catch (error) {
      console.error("Error fetching token balances: ", error);

      toast.error(
        error.response?.data?.message || "Failed to load token balances.",
      );

      setTokenBalances([]);
    } finally {
      setLoadingTokens(false);
    }
  }, [user?._id]);

  // Helper to compute individual token monetary values
  const calculateTokenData = useCallback((entry) => {
    const buyPrice = entry?.price?.buy || 0;
    const currency = entry?.currency || "NGN";

    const totalBalanceVal = (entry?.balance || 0) * buyPrice;
    const escrowBalanceVal = (entry?.escrow || 0) * buyPrice;

    return {
      symbol: entry?.symbol || "N/A",
      name: entry?.name || "Unknown Token",
      currency,
      rawBalanceCount: entry?.balance || 0,
      rawEscrowCount: entry?.escrow || 0,
      balanceVal: totalBalanceVal,
      escrowVal: escrowBalanceVal,
      formattedBalance: `${currency} ${totalBalanceVal.toLocaleString()}`,
      formattedEscrow: `${currency} ${escrowBalanceVal.toLocaleString()}`,
    };
  }, []);

  // Compute overall totals from the API response array
  const { processedTokens, totalPortfolioValue } = useMemo(() => {
    let totalValue = 0;

    const tokens = tokenBalances.map((entry) => {
      const data = calculateTokenData(entry);

      totalValue += data.balanceVal + data.escrowVal;

      return data;
    });

    return {
      processedTokens: tokens,
      totalPortfolioValue: totalValue,
    };
  }, [tokenBalances, calculateTokenData]);

  // Fetch Member Transactions
  const fetchMemberTransactions = useCallback(
    async (currentPage = 1) => {
      if (!user?._id) return;

      try {
        setLoadingTransactions(true);

        const response = await axiosInstance.get(
          "/marketplace/getequity/get-member-transactions/",
          {
            params: {
              member_id: user._id,
              page: currentPage,
              limit: PAGE_SIZE,
            },
          },
        );

        const resData = response.data;
        const payload = resData?.data ?? {};

        /*
         * Supports responses such as:
         *
         * data: [...]
         *
         * data: {
         *   transactions: [...],
         *   totalPages: 5
         * }
         *
         * data: {
         *   docs: [...],
         *   totalDocs: 50
         * }
         */
        const list = Array.isArray(payload)
          ? payload
          : payload.transactions ||
            payload.docs ||
            payload.results ||
            payload.items ||
            [];

        const totalRecords = Number(
          payload.totalDocs ??
            payload.totalItems ??
            payload.total ??
            resData?.totalDocs ??
            resData?.totalItems ??
            resData?.total ??
            0,
        );

        const calculatedTotalPages =
          totalRecords > 0 ? Math.ceil(totalRecords / PAGE_SIZE) : 1;

        const apiTotalPages = Number(
          payload.totalPages ??
            payload.pages ??
            resData?.totalPages ??
            resData?.pages ??
            calculatedTotalPages,
        );

        setTransactions(Array.isArray(list) ? list : []);
        setTotalPages(Math.max(apiTotalPages || 1, 1));
        setPage(currentPage);
      } catch (error) {
        console.error("Error fetching member transactions: ", error);

        toast.error(
          error.response?.data?.message || "Failed to load transactions.",
        );

        setTransactions([]);
        setTotalPages(1);
      } finally {
        setLoadingTransactions(false);
      }
    },
    [user?._id],
  );

  // Fetch Member Orders
  const fetchMemberOrders = useCallback(
    async (currentPage = 1) => {
      if (!user?._id) return;

      try {
        setLoadingOrders(true);

        const response = await axiosInstance.get(
          "/marketplace/getequity/get-member-orders/",
          {
            params: {
              member_id: user._id,
              page: currentPage,
              limit: PAGE_SIZE,
            },
          },
        );

        const resData = response.data;
        const payload = resData?.data ?? {};

        /*
         * Supports responses such as:
         *
         * data: [...]
         *
         * data: {
         *   orders: [...],
         *   totalPages: 5
         * }
         *
         * data: {
         *   docs: [...],
         *   totalDocs: 50
         * }
         */
        const list = Array.isArray(payload)
          ? payload
          : payload.orders ||
            payload.docs ||
            payload.results ||
            payload.items ||
            [];

        const totalRecords = Number(
          payload.totalDocs ??
            payload.totalItems ??
            payload.total ??
            resData?.totalDocs ??
            resData?.totalItems ??
            resData?.total ??
            0,
        );

        const calculatedTotalPages =
          totalRecords > 0 ? Math.ceil(totalRecords / PAGE_SIZE) : 1;

        const apiTotalPages = Number(
          payload.totalPages ??
            payload.pages ??
            resData?.totalPages ??
            resData?.pages ??
            calculatedTotalPages,
        );

        setOrders(Array.isArray(list) ? list : []);
        setOrdersTotalPages(Math.max(apiTotalPages || 1, 1));
        setOrdersPage(currentPage);
      } catch (error) {
        console.error("Error fetching member orders: ", error);

        toast.error(error.response?.data?.message || "Failed to load orders.");

        setOrders([]);
        setOrdersTotalPages(1);
      } finally {
        setLoadingOrders(false);
      }
    },
    [user?._id],
  );

  // Fetch everything whenever the modal is opened for a member
  useEffect(() => {
    if (open && user?._id) {
      setPage(1);
      setOrdersPage(1);

      fetchWalletBalance();
      fetchMemberTokenBalances();
      fetchMemberTransactions(1);
      fetchMemberOrders(1);
    }
  }, [
    open,
    user?._id,
    fetchWalletBalance,
    fetchMemberTokenBalances,
    fetchMemberTransactions,
    fetchMemberOrders,
  ]);

  // Handle transaction page change
  const handlePageChange = (event, value) => {
    if (loadingTransactions || value === page) return;

    fetchMemberTransactions(value);
  };

  // Handle orders page change
  const handleOrdersPageChange = (event, value) => {
    if (loadingOrders || value === ordersPage) return;

    fetchMemberOrders(value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 0,
          border: "2px solid #000000",
          bgcolor: "#ffffff",
          color: "#000000",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "2px solid #000000",
          pb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              textTransform: "uppercase",
            }}
          >
            Member Profile
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: "#666666",
            }}
          >
            ID: {user?._id || "N/A"}
          </Typography>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            color: "#000000",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* User Quick Info */}
        <Box sx={{ mb: 3, mt: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 1,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                }}
              >
                {user.fname || user.lname
                  ? `${user.fname || ""} ${user.lname || ""}`.trim()
                  : "N/A"}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#444444",
                }}
              >
                {user.email || "N/A"} | {user.phone || "N/A"}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: "#666666",
                }}
              >
                {user.homeAddress ? `${user.homeAddress}, ` : ""}
                {user.city ? `${user.city}, ` : ""}
                {user.state ? `${user.state}, ` : ""}
                {user.country || ""}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1,
              }}
            >
              <Chip
                label={member?.status || "Unknown"}
                size="small"
                sx={{
                  borderRadius: 0,
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  bgcolor: "#000000",
                  color: "#ffffff",
                }}
              />

              <Chip
                label={
                  user.verified ? (
                    <FontAwesomeIcon icon={faCheck} color="green" />
                  ) : (
                    <FontAwesomeIcon icon={faCancel} color="red" />
                  )
                }
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: 0,
                  borderColor: "#000000",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                }}
              />
            </Box>
          </Box>
        </Box>

        <Divider
          sx={{
            mb: 3,
            borderColor: "#000000",
          }}
        />

        {/* Financial Overview Summary */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Wallet Balance Card */}
          <Grid item xs={12} sm={6}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid #000000",
                borderRadius: 0,
                bgcolor: "#fcfcfc",
              }}
            >
              <CardContent
                sx={{
                  p: 2,
                  "&:last-child": {
                    pb: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    color: "#666666",
                  }}
                >
                  <AccountBalanceWalletIcon fontSize="small" />

                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Wallet Balance (GNGN)
                  </Typography>
                </Box>

                {loadingBalance ? (
                  <CircularProgress
                    size={20}
                    sx={{
                      color: "#000000",
                      my: 1,
                    }}
                  />
                ) : (
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    NGN{" "}
                    {walletBalance !== null
                      ? walletBalance.toLocaleString()
                      : "0"}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Aggregate Token Portfolio Value */}
          <Grid item xs={12} sm={6}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid #000000",
                borderRadius: 0,
                bgcolor: "#fcfcfc",
              }}
            >
              <CardContent
                sx={{
                  p: 2,
                  "&:last-child": {
                    pb: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    color: "#666666",
                  }}
                >
                  <TollIcon fontSize="small" />

                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Total Token Portfolio Value
                  </Typography>
                </Box>

                {loadingTokens ? (
                  <CircularProgress
                    size={20}
                    sx={{
                      color: "#000000",
                      my: 1,
                    }}
                  />
                ) : (
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    NGN {totalPortfolioValue.toLocaleString()}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Detailed Token Balances Grid */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              textTransform: "uppercase",
              mb: 1.5,
            }}
          >
            Token Holdings & Escrow
          </Typography>

          {loadingTokens ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                p: 2,
              }}
            >
              <CircularProgress
                size={24}
                sx={{
                  color: "#000000",
                }}
              />
            </Box>
          ) : processedTokens.length === 0 ? (
            <Typography
              variant="body2"
              sx={{
                color: "#666666",
                fontStyle: "italic",
              }}
            >
              No token balances found for this member.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {processedTokens.map((token, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={`${token.symbol}-${index}`}
                >
                  <Card
                    elevation={0}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 0,
                      bgcolor: "#ffffff",
                      p: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 800,
                        }}
                      >
                        {token.name}
                      </Typography>

                      <Chip
                        label={token.symbol}
                        size="small"
                        sx={{
                          borderRadius: 0,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          height: 20,
                        }}
                      />
                    </Box>

                    <Box sx={{ mb: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#666666",
                          display: "block",
                        }}
                      >
                        Balance ({token.rawBalanceCount.toLocaleString()}{" "}
                        units):
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        {token.formattedBalance}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#666666",
                          display: "block",
                        }}
                      >
                        Escrow ({token.rawEscrowCount.toLocaleString()} units):
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: "#d32f2f",
                        }}
                      >
                        {token.formattedEscrow}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Tabbed Activity Sections */}
        <Box sx={{ width: "100%", mt: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(event, newValue) => setActiveTab(newValue)}
            sx={{
              borderBottom: "1px solid #000000",
              "& .MuiTab-root": {
                borderRadius: 0,
                color: "#000000",
                fontWeight: 700,
                "&.Mui-selected": {
                  color: "#000000",
                },
              },
              "& .MuiTabs-indicator": {
                bgcolor: "#000000",
                height: "3px",
              },
            }}
          >
            <Tab label="Orders" />
            <Tab label="Transactions" />
          </Tabs>

          {/* Orders Section */}
          {activeTab === 0 && (
            <Box sx={{ mt: 2 }}>
              {loadingOrders ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    p: 4,
                  }}
                >
                  <CircularProgress
                    sx={{
                      color: "#000000",
                    }}
                  />
                </Box>
              ) : orders.length === 0 ? (
                <Box
                  sx={{
                    p: 4,
                    textAlign: "center",
                    border: "1px dashed #cccccc",
                    bgcolor: "#fafafa",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No orders found for this member.
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      border: "1px solid #000000",
                      borderRadius: 0,
                      overflowX: "auto",
                    }}
                  >
                    <Table size="small">
                      <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 800 }}>Type</TableCell>

                          <TableCell sx={{ fontWeight: 800 }}>
                            Asset / Token
                          </TableCell>

                          <TableCell sx={{ fontWeight: 800 }}>Price</TableCell>

                          <TableCell sx={{ fontWeight: 800 }}>Amount</TableCell>

                          <TableCell sx={{ fontWeight: 800 }}>Total</TableCell>

                          <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>

                          <TableCell sx={{ fontWeight: 800 }}>Date</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {orders.map((order, index) => {
                          const currency = order.currency || "NGN";
                          const price = Number(order.price) || 0;
                          const amount = Number(order.amount) || 0;
                          const total = price * amount;

                          return (
                            <TableRow
                              key={
                                order._id ||
                                order.id ||
                                `${ordersPage}-${index}`
                              }
                            >
                              <TableCell>
                                <Chip
                                  label={order.type || "N/A"}
                                  size="small"
                                  sx={{
                                    borderRadius: 0,
                                    fontWeight: 700,
                                    fontSize: "0.65rem",
                                    bgcolor:
                                      order.type?.toLowerCase() === "buy"
                                        ? "#000000"
                                        : "#666666",
                                    color: "#ffffff",
                                  }}
                                />
                              </TableCell>

                              <TableCell sx={{ fontWeight: 600 }}>
                                {order.token?.name ||
                                  order.token?.symbol ||
                                  "N/A"}
                              </TableCell>

                              <TableCell>
                                {currency} {price.toLocaleString()}
                              </TableCell>

                              <TableCell>{amount.toLocaleString()}</TableCell>

                              <TableCell sx={{ fontWeight: 700 }}>
                                {currency} {total.toLocaleString()}
                              </TableCell>

                              <TableCell>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 800,
                                    color:
                                      order.status?.toLowerCase() ===
                                        "active" ||
                                      order.status?.toLowerCase() ===
                                        "completed"
                                        ? "#2e7d32"
                                        : order.status?.toLowerCase() ===
                                              "cancelled" ||
                                            order.status?.toLowerCase() ===
                                              "failed"
                                          ? "#d32f2f"
                                          : "#ed6c02",
                                  }}
                                >
                                  {order.status?.toUpperCase() || "PENDING"}
                                </Typography>
                              </TableCell>

                              <TableCell sx={{ fontSize: "0.75rem" }}>
                                {order.createdAt
                                  ? new Date(
                                      order.createdAt,
                                    ).toLocaleDateString()
                                  : "N/A"}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {ordersTotalPages > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        mt: 2,
                      }}
                    >
                      <Pagination
                        count={ordersTotalPages}
                        page={ordersPage}
                        onChange={handleOrdersPageChange}
                        disabled={loadingOrders}
                        showFirstButton
                        showLastButton
                        siblingCount={1}
                        boundaryCount={1}
                        size="small"
                        sx={{
                          "& .MuiPaginationItem-root": {
                            borderRadius: 0,
                          },
                          "& .Mui-selected": {
                            bgcolor: "#000000 !important",
                            color: "#ffffff",
                          },
                        }}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}

          {/* Transactions Section */}
          {activeTab === 1 && (
            <Box sx={{ mt: 2 }}>
              {loadingTransactions ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    p: 4,
                  }}
                >
                  <CircularProgress
                    sx={{
                      color: "#000000",
                    }}
                  />
                </Box>
              ) : transactions.length === 0 ? (
                <Box
                  sx={{
                    p: 4,
                    textAlign: "center",
                    border: "1px dashed #cccccc",
                    bgcolor: "#fafafa",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No transaction records found for this member.
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      border: "1px solid #000000",
                      borderRadius: 0,
                      overflowX: "auto",
                    }}
                  >
                    <Table size="small">
                      <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 800 }}>Type</TableCell>

                          <TableCell sx={{ fontWeight: 800 }}>
                            Asset / Token
                          </TableCell>

                          <TableCell sx={{ fontWeight: 800 }}>
                            Amount / Volume
                          </TableCell>

                          <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>

                          <TableCell sx={{ fontWeight: 800 }}>Date</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {transactions.map((tx, index) => {
                          const displayAmount = tx.nairaValue ?? tx.volume ?? 0;

                          const currency =
                            tx.currency || tx.token?.currency || "";

                          return (
                            <TableRow
                              key={tx._id || tx.id || `${page}-${index}`}
                            >
                              <TableCell>
                                <Chip
                                  label={tx.type || "N/A"}
                                  size="small"
                                  sx={{
                                    borderRadius: 0,
                                    fontWeight: 700,
                                    fontSize: "0.65rem",
                                    bgcolor: "#000000",
                                    color: "#ffffff",
                                  }}
                                />
                              </TableCell>

                              <TableCell sx={{ fontWeight: 600 }}>
                                {tx.token?.name || tx.token?.symbol || "N/A"}
                              </TableCell>

                              <TableCell sx={{ fontWeight: 700 }}>
                                {Number(displayAmount).toLocaleString()}
                              </TableCell>

                              <TableCell>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 800,
                                    color:
                                      tx.status?.toLowerCase() ===
                                        "successful" ||
                                      tx.status?.toLowerCase() === "completed"
                                        ? "#2e7d32"
                                        : tx.status?.toLowerCase() === "failed"
                                          ? "#d32f2f"
                                          : "#ed6c02",
                                  }}
                                >
                                  {tx.status?.toUpperCase() || "PENDING"}
                                </Typography>
                              </TableCell>

                              <TableCell sx={{ fontSize: "0.75rem" }}>
                                {tx.createdAt
                                  ? new Date(tx.createdAt).toLocaleDateString()
                                  : "N/A"}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {totalPages > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        mt: 2,
                      }}
                    >
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        disabled={loadingTransactions}
                        showFirstButton
                        showLastButton
                        siblingCount={1}
                        boundaryCount={1}
                        size="small"
                        sx={{
                          "& .MuiPaginationItem-root": {
                            borderRadius: 0,
                          },
                          "& .Mui-selected": {
                            bgcolor: "#000000 !important",
                            color: "#ffffff",
                          },
                        }}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Box
          onClick={onClose}
          sx={{
            px: 3,
            py: 1,
            bgcolor: "#000000",
            color: "#ffffff",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: "0.875rem",
            "&:hover": {
              bgcolor: "#333333",
            },
          }}
        >
          Close
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default MemberDetailsModal;
